import React from 'react';
import { Stage, Layer, Rect, Text as KonvaText, Image as KonvaImage, Circle, Line, Group } from 'react-konva';
import useImage from 'use-image';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const SAMPLE_DATA = {
  price: '$875,000',
  address: '2847 Sunset Blvd, Beverly Hills',
  beds: '4',
  baths: '3',
  sqft: '2,450',
  summary: 'Stunning modern home with panoramic views',
  key_features: 'Pool, Smart Home, Gourmet Kitchen',
};

const buildDataMap = (propertyData) => {
  if (!propertyData) return SAMPLE_DATA;
  const features = propertyData.key_features;
  return {
    price: propertyData.price || SAMPLE_DATA.price,
    address: propertyData.address || SAMPLE_DATA.address,
    beds: propertyData.beds || SAMPLE_DATA.beds,
    baths: propertyData.baths || SAMPLE_DATA.baths,
    sqft: propertyData.sqft || SAMPLE_DATA.sqft,
    summary: propertyData.property_summary || propertyData.description || SAMPLE_DATA.summary,
    key_features: Array.isArray(features) ? features.join(', ') : (features || SAMPLE_DATA.key_features),
  };
};

const fillPlaceholders = (text, dataMap) => {
  if (!text) return text;
  return text.replace(/\$?\{\{(\w+)\}\}/g, (_, key) => dataMap[key] || `{{${key}}}`);
};

const proxyUrl = (url) => {
  if (!url || url.startsWith('data:') || url.startsWith('blob:') || url.includes('/api/proxy-image')) return url;
  if (url.startsWith('http')) {
    let token = '';
    try { token = window.localStorage.getItem('token') || ''; } catch (e) { token = ''; }
    const tokenParam = token ? `&token=${encodeURIComponent(token)}` : '';
    return `${API_BASE}/api/proxy-image?url=${encodeURIComponent(url)}${tokenParam}`;
  }
  return url;
};

const ThumbnailImage = ({ el, scaleX, scaleY }) => {
  const [img] = useImage(proxyUrl(el.src), 'anonymous');
  if (!img) return null;
  const targetW = (el.width || 200) * scaleX;
  const targetH = (el.height || 150) * scaleY;
  const isLogo = el.isLogoFromBrandKit || el.originalType === 'logoPlaceholder';
  if (isLogo) {
    const imgW = img.naturalWidth || img.width;
    const imgH = img.naturalHeight || img.height;
    const scale = Math.min(targetW / imgW, targetH / imgH);
    const drawW = imgW * scale;
    const drawH = imgH * scale;
    const offX = (targetW - drawW) / 2;
    const offY = (targetH - drawH) / 2;
    return <KonvaImage image={img} x={el.x * scaleX + offX} y={el.y * scaleY + offY} width={drawW} height={drawH} />;
  }
  return (
    <KonvaImage image={img}
      x={el.x * scaleX} y={el.y * scaleY}
      width={targetW} height={targetH} />
  );
};

const SAMPLE_PHOTO = 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=400&q=60';
const PhotoFill = ({ x, y, w, h, src }) => {
  const [img] = useImage(proxyUrl(src || SAMPLE_PHOTO), 'anonymous');
  if (!img) return <Rect x={x} y={y} width={w} height={h} fill="#E8D5B7" />;
  return <KonvaImage image={img} x={x} y={y} width={w} height={h} />;
};

export const TemplateThumbnail = ({ canvasData, width = 280, onClick, propertyData = null }) => {
  if (!canvasData) return null;

  const cw = canvasData.width || 1080;
  const ch = canvasData.height || 1080;
  const aspectRatio = cw / ch;
  const thumbW = width;
  const thumbH = thumbW / aspectRatio;
  const sx = thumbW / cw;
  const sy = thumbH / ch;
  const bgColor = canvasData.backgroundColor || '#FFFFFF';
  const elements = canvasData.elements || [];

  const dataMap = buildDataMap(propertyData);
  const photoSrc = propertyData?.main_image_url
    || (Array.isArray(propertyData?.photos) && propertyData.photos[0])
    || SAMPLE_PHOTO;
  const extraPhotos = Array.isArray(propertyData?.photos) ? propertyData.photos : [];

  return (
    <div className="w-full cursor-pointer" onClick={onClick}
      style={{ height: thumbH }} data-testid="template-thumbnail">
      <Stage width={thumbW} height={thumbH} listening={false}>
        <Layer>
          <Rect x={0} y={0} width={thumbW} height={thumbH} fill={bgColor} />
          {elements.map((el, i) => {
            if (el.visible === false) return null;

            if (el.type === 'rect') {
              return <Rect key={i} x={el.x * sx} y={el.y * sy}
                width={(el.width || 100) * sx} height={(el.height || 100) * sy}
                fill={el.fill} stroke={el.stroke} strokeWidth={(el.strokeWidth || 0) * Math.min(sx, sy)}
                cornerRadius={el.cornerRadius ? el.cornerRadius * Math.min(sx, sy) : 0}
                opacity={el.opacity ?? 1} />;
            }
            if (el.type === 'circle') {
              return <Circle key={i} x={el.x * sx} y={el.y * sy}
                radius={(el.radius || 50) * Math.min(sx, sy)}
                fill={el.fill} stroke={el.stroke} opacity={el.opacity ?? 1} />;
            }
            if (el.type === 'line') {
              const pts = (el.points || []).map((p, j) => j % 2 === 0 ? p * sx : p * sy);
              return <Line key={i} points={pts} stroke={el.stroke} strokeWidth={(el.strokeWidth || 2) * Math.min(sx, sy)}
                opacity={el.opacity ?? 1} />;
            }
            if (el.type === 'text') {
              return <KonvaText key={i} x={el.x * sx} y={el.y * sy}
                text={fillPlaceholders(el.text, dataMap)} fontSize={(el.fontSize || 24) * Math.min(sx, sy)}
                fontFamily={el.fontFamily || 'Arial'} fill={el.fill || '#000'}
                fontStyle={el.fontStyle} align={el.align}
                width={el.width ? el.width * sx : undefined}
                opacity={el.opacity ?? 1} />;
            }
            if (el.type === 'image' && el.src) {
              return <ThumbnailImage key={i} el={el} scaleX={sx} scaleY={sy} />;
            }
            if (el.type === 'photoPlaceholder') {
              return (
                <PhotoFill key={i} src={photoSrc}
                  x={el.x * sx} y={el.y * sy}
                  w={(el.width || 200) * sx} h={(el.height || 200) * sy} />
              );
            }
            if (el.type === 'photoGrid' && el.slots) {
              return (
                <Group key={i}>
                  {el.slots.map((slot, si) => (
                    <PhotoFill key={si} src={extraPhotos[si] || photoSrc}
                      x={slot.x * sx} y={slot.y * sy}
                      w={slot.width * sx} h={slot.height * sy} />
                  ))}
                </Group>
              );
            }
            if (el.type === 'logoPlaceholder') {
              return (
                <Group key={i}>
                  <Rect x={el.x * sx} y={el.y * sy}
                    width={(el.width || 100) * sx} height={(el.height || 50) * sy}
                    fill={el.backgroundColor || '#E5E5E5'} cornerRadius={2} />
                  <KonvaText x={el.x * sx} y={(el.y + (el.height || 50) / 2 - 5) * sy}
                    width={(el.width || 100) * sx}
                    text="LOGO" fontSize={8 * Math.min(sx, sy)} fill="#666" align="center" />
                </Group>
              );
            }
            if (el.type === 'headshotPlaceholder') {
              const w = (el.width || 100);
              const h = (el.height || 100);
              return (
                <Group key={i}>
                  <Rect x={el.x * sx} y={el.y * sy}
                    width={w * sx} height={h * sy}
                    fill={el.backgroundColor || '#F5F5F5'}
                    cornerRadius={(el.cornerRadius != null ? el.cornerRadius : Math.min(w, h) / 2) * Math.min(sx, sy)} />
                  <KonvaText x={el.x * sx} y={(el.y + h / 2 - 5) * sy}
                    width={w * sx}
                    text="HEADSHOT" fontSize={7 * Math.min(sx, sy)} fill="#666" align="center" />
                </Group>
              );
            }
            return null;
          })}
        </Layer>
      </Stage>
    </div>
  );
};
